import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Plus, Mail, Phone, MessageCircle } from 'lucide-react';
import { MaskLine, FadeUp, EASE } from '@/components/Reveal';
import { DubaiClock } from '@/components/DubaiClock';
import { CONTACT, FOUNDERS, FAQS } from '@/data/content';

const SERVICE_OPTIONS = ['Content Strategy', 'Copywriting', 'Video Content', 'Graphic Design', 'Social Media'];
const BUDGET_OPTIONS = ['AED 3K – 5K', 'AED 5K – 9K', 'AED 10K – 15K', 'AED 15K+'];
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mrpbvpyy';
const NEXT_STEPS = [
  'We read your brief properly — every line of it.',
  'A quick call to understand your brand, audience and goals.',
  'A tailored proposal with scope, timeline and budget.',
];

const inputCls =
  'w-full bg-parchment border border-ink/20 rounded-xl px-4 py-3.5 text-ink placeholder:text-ink/35 outline-none focus:border-ink transition-colors duration-300';

const FaqItem = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`faq-item-${i}`} className="border-b border-ink/15">
      <button
        data-testid={`faq-question-${i}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 py-6 text-left"
      >
        <span className="font-slab font-bold text-lg sm:text-xl">{q}</span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3 }}
          className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center ${open ? 'bg-butter border-ink' : 'border-ink/25'}`}
        >
          <Plus size={16} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <p data-testid={`faq-answer-${i}`} className="pb-7 text-ink/70 leading-relaxed max-w-2xl">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [services, setServices] = useState([]);
  const [budget, setBudget] = useState(BUDGET_OPTIONS[1]);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in your name, email and a few words about the project.');
      return;
    }
    setError('');
    setSending(true);
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(e.currentTarget),
      });
      if (!res.ok) throw new Error('Formspree error');
      setSent(true);
      setForm({ name: '', email: '', company: '', message: '' });
      setServices([]);
    } catch {
      setError(`Something went wrong sending your brief. Please try again or email us at ${CONTACT.email}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      {/* HERO */}
      <section className="px-6 sm:px-12 pt-40 pb-16 sm:pb-24">
        <div className="max-w-[1500px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7, ease: EASE }}
            className="flex flex-wrap items-center gap-6 mb-8"
          >
            <p className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold">Get in Touch</p>
            <DubaiClock />
          </motion.div>
          <h1 data-testid="contact-headline" className="font-slab font-black leading-[0.98] tracking-tight text-[11vw] sm:text-[8.5vw] lg:text-[6.5vw]">
            <MaskLine delay={0.2}>LET’S MAKE</MaskLine>
            <MaskLine delay={0.34}>SOMETHING</MaskLine>
            <MaskLine delay={0.48}><span className="text-royal">GREAT TOGETHER.</span></MaskLine>
          </h1>
        </div>
      </section>

      {/* FORM + DETAILS */}
      <section className="px-6 sm:px-12 pb-24">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-5 gap-12 lg:gap-16">
          <FadeUp className="lg:col-span-3">
            <form data-testid="contact-form" action={FORMSPREE_ENDPOINT} method="POST" onSubmit={submit} className="space-y-6">
              <input type="hidden" name="_subject" value={`New Project Enquiry — ${form.company || form.name || 'Butter Media website'}`} />
              <input type="hidden" name="services" value={services.length ? services.join(', ') : 'Not sure yet'} />
              <input type="hidden" name="budget" value={budget} />
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="cf-name" className="block text-[11px] uppercase tracking-[0.25em] font-medium text-ink/60 mb-2.5">Full Name *</label>
                  <input id="cf-name" name="name" type="text" autoComplete="name" data-testid="contact-form-name-input" className={inputCls} placeholder="Your name"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="cf-email" className="block text-[11px] uppercase tracking-[0.25em] font-medium text-ink/60 mb-2.5">Email / WhatsApp *</label>
                  <input id="cf-email" name="email" type="text" autoComplete="email" data-testid="contact-form-email-input" className={inputCls} placeholder="you@brand.com"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>

              <div>
                <label htmlFor="cf-company" className="block text-[11px] uppercase tracking-[0.25em] font-medium text-ink/60 mb-2.5">Company / Brand</label>
                <input id="cf-company" name="company" type="text" autoComplete="organization" data-testid="contact-form-company-input" className={inputCls} placeholder="Your brand"
                  value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] font-medium text-ink/60 mb-3">What do you need?</p>
                <div className="flex flex-wrap gap-2.5">
                  {SERVICE_OPTIONS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      data-testid={`contact-service-${s.toLowerCase().replace(/\s/g, '-')}`}
                      onClick={() => toggleService(s)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all duration-300 ${
                        services.includes(s) ? 'bg-royal text-cream border-royal' : 'border-ink/25 text-ink/65 hover:border-ink hover:text-ink'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] font-medium text-ink/60 mb-3">Estimated budget</p>
                <div className="flex flex-wrap gap-2.5">
                  {BUDGET_OPTIONS.map((b, i) => (
                    <button
                      type="button"
                      key={b}
                      data-testid={`contact-budget-${i}`}
                      onClick={() => setBudget(b)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all duration-300 ${
                        budget === b ? 'bg-ink text-cream border-ink' : 'border-ink/25 text-ink/65 hover:border-ink hover:text-ink'
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="cf-message" className="block text-[11px] uppercase tracking-[0.25em] font-medium text-ink/60 mb-2.5">Project scope & timeline *</label>
                <textarea id="cf-message" name="message" data-testid="contact-form-message-input" rows={5} className={`${inputCls} resize-none`}
                  placeholder="Tell us about the story you want to tell…"
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>

              {error && <p data-testid="contact-form-error" className="text-sm font-medium text-royal">{error}</p>}
              {sent && (
                <p data-testid="contact-form-sent" className="text-sm font-medium text-ink/70 bg-butter border border-ink rounded-xl px-4 py-3">
                  Brief received — thank you. We’ll be in touch within a working day.
                </p>
              )}

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="group inline-flex items-center gap-3 bg-ink text-cream font-slab font-bold text-lg px-10 py-5 rounded-full hover:bg-royal transition-colors duration-300 disabled:opacity-60 disabled:cursor-wait"
              >
                {sending ? 'Sending…' : 'Send the Brief'}
                <ArrowUpRight size={20} className="group-hover:rotate-45 transition-transform duration-300" />
              </button>
            </form>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-2">
            <div className="space-y-6">
              <div className="bg-ink text-cream rounded-[1.5rem] p-8 sm:p-10">
                <p className="text-[11px] uppercase tracking-[0.25em] text-butter font-bold mb-6">Talk to the founders</p>
                <div className="space-y-7">
                  {FOUNDERS.map((f) => (
                    <div key={f.email} data-testid={`founder-${f.name.split(' ')[0].toLowerCase()}`} className="border-t border-cream/15 pt-6 first:border-t-0 first:pt-0">
                      <p className="font-slab font-bold text-xl">{f.name}</p>
                      <p className="text-cream/55 text-sm mb-3">{f.role}</p>
                      <a href={`tel:${f.phone.replace(/\s/g, '')}`} className="flex items-center gap-2.5 text-sm text-cream/80 hover:text-butter transition-colors">
                        <Phone size={14} /> {f.phone}
                      </a>
                      <a href={`mailto:${f.email}`} className="flex items-center gap-2.5 text-sm text-cream/80 hover:text-butter transition-colors mt-1.5">
                        <Mail size={14} /> {f.email}
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              <div data-testid="contact-next-steps" className="bg-butter border border-ink rounded-[1.5rem] p-8 sm:p-10">
                <p className="text-[11px] uppercase tracking-[0.25em] font-bold mb-6 text-ink/60">What happens next</p>
                <ol className="space-y-4">
                  {NEXT_STEPS.map((s, i) => (
                    <li key={s} className="flex gap-4 items-start">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-ink text-cream text-xs font-slab font-bold flex items-center justify-center">{i + 1}</span>
                      <span className="text-ink/80 leading-snug pt-0.5">{s}</span>
                    </li>
                  ))}
                </ol>
                <a
                  href={`https://wa.me/${FOUNDERS[0].phone.replace(/\D/g, '')}?text=${encodeURIComponent('Hi Butter Media — I’d like to talk about a project.')}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="group mt-8 inline-flex items-center gap-2.5 bg-ink text-cream font-slab font-bold px-6 py-3.5 rounded-full hover:bg-royal transition-colors duration-300"
                >
                  <MessageCircle size={18} /> Prefer to chat? WhatsApp us
                  <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
                </a>
                <p className="font-script text-royal text-3xl mt-6 -rotate-2 block w-max">talk soon.</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="px-6 sm:px-12 pb-16">
        <div className="max-w-[1000px] mx-auto">
          <FadeUp>
            <p className="text-[11px] uppercase tracking-[0.3em] text-royal font-bold mb-5">Before you ask</p>
            <h2 className="font-slab font-black text-4xl sm:text-5xl leading-[1.02] tracking-tight mb-10">
              Questions, answered.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="border-t border-ink/15">
              {FAQS.map((f, i) => (
                <FaqItem key={f.q} q={f.q} a={f.a} i={i} />
              ))}
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
